/**
 * Tiny pub-sub state store. Sections subscribe to one or more top-level keys
 * and re-render only when those keys change.
 *
 * Usage:
 *   import { state, set, subscribe } from '../state/store.js';
 *   subscribe(['cart','promo'], () => render());
 *   set({ promo: { code: 'X', type: 'percent', value: 10 } });
 *
 * Patches are shallow-merged at the top level (state[key] is replaced wholesale).
 * That means callers should pass a new object/array rather than mutating in place.
 */

import { DELIVERY_METHODS } from '../config/delivery.js';
import { PAYMENT_METHODS } from '../config/payment.js';

export const state = {
  tab: 'self',
  contact: {},
  delivery: {
    methodId: DELIVERY_METHODS[0]?.id ?? null,
    subOptionId: DELIVERY_METHODS[0]?.subOptions?.[0]?.id ?? null,
    // City selected from carrier autocomplete.
    cityRef: '',         // NP: DeliveryCity UUID; UP: CITY_ID
    settlementRef: '',   // NP: settlement Ref UUID for street search; UP: same as cityRef
    cityLabel: '',       // display string ("м. Київ, Київська обл.")
    // UP-specific (NP carries everything in the refs above).
    regionId: '',        // UP REGION_ID — required for /get_postoffices_by_city_id
    districtId: '',      // UP DISTRICT_ID — kept for future address-derivation calls
    // Branch / parcel locker.
    warehouseRef: '',
    warehouseLabel: '',
    // Address (адресна доставка).
    streetRef: '',
    streetLabel: '',
    building: '',
    apartment: '',
    // Derived postcode for UP адресна доставка (looked up from streetRef + building).
    postcode: '',
    // Ukrposhta requires the recipient's patronymic (По батькові). Lives here
    // because it's a carrier-specific requirement, not a general contact field.
    patronymic: '',
  },
  payment: {
    methodId: PAYMENT_METHODS[0]?.id ?? null,
  },
  cart: {
    id: null,
    lines: [],
    subtotal: 0,
    currency: 'грн',
  },
  upsells: [],
  promo: null,
  // Customer data forwarded from chekly redirect (shop URL params).
  shop: {
    storeName: null,
    cartToken: null,
    curRate: 1,
    theme: null,
    shopifyDomain: null,
    prefill: {},
    tracking: {},
  },
  ui: {
    submitting: false,
    errors: {},
  },
};

const subscribers = new Set();

// Debug hook — expose state on window so you can inspect it from DevTools.
// Read-only convenience; never mutate this directly, always go through set/patch.
if (typeof window !== 'undefined') {
  window.__state = state;
}

/**
 * @param {string[] | null} keys  Top-level state keys to watch. `null` = all changes.
 * @param {() => void} fn         Called whenever any watched key is patched.
 * @returns {() => void}          Unsubscribe.
 */
export function subscribe(keys, fn) {
  const entry = { keys: keys ? new Set(keys) : null, fn };
  subscribers.add(entry);
  return () => subscribers.delete(entry);
}

/**
 * Shallow-merge a patch into state and notify matching subscribers.
 */
export function set(patch) {
  const changed = new Set();
  for (const k of Object.keys(patch)) {
    if (state[k] !== patch[k]) {
      state[k] = patch[k];
      changed.add(k);
    }
  }
  if (changed.size === 0) return;
  for (const { keys, fn } of subscribers) {
    if (!keys) { fn(); continue; }
    for (const k of changed) {
      if (keys.has(k)) { fn(); break; }
    }
  }
}

/**
 * Helper for deep-ish patches into a single key (e.g. set merging contact fields).
 */
export function patch(key, partial) {
  set({ [key]: { ...state[key], ...partial } });
}
